import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {pluck, switchMap, tap} from 'rxjs/operators';
import {TimecardRateType, TimecardStatus} from '../../../model';
import {AnalyticsService} from '../../shared/analytics.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {ModalComponent} from '../../shared/modal/modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {PdfModalComponent} from '../pdf-modal/pdf-modal.component';

@Component({
  selector: 'timecard-details-page',
  templateUrl: './timecard-details-page.component.html',
  styleUrls: ['./timecard-details-page.component.scss']
})
export class TimecardDetailsPageComponent implements OnInit {

  timecard: TimecardDetails;
  timecardDays: TimecardDay[] = [];
  error = false;
  disputeSubmitting = false;
  reason: string;

  view$: Observable<string>;
  detailsExpanded$ = new BehaviorSubject(false);
  pdf$: Observable<Blob>;
  masquerading$ = this.Auth.masquerading$;

  @ViewChild('disputeModal') disputeModal: ModalComponent;
  @ViewChild('disputeSuccessModal') disputeSuccessModal: ModalComponent;
  @ViewChild('pdfModal') pdfModal: PdfModalComponent;

  Status = TimecardStatus;
  RateType = TimecardRateType;

  constructor(private route: ActivatedRoute,
              private Navigation: NavigationService,
              private Api: ClientApiService,
              private Auth: AuthService,
              private Analytics: AnalyticsService,
              private AppState: AppStateService) {
  }

  ngOnInit() {
    this.view$ = this.route.queryParams.pipe(pluck('view'));

    this.route.paramMap
      .pipe(
        tap(params => this.AppState.loading$.next(true)),
        switchMap(params => this.Api.getTimecard(+params.get('id')))
      )
      .subscribe(data => {
        this.timecard = data;
        this.populateTimecardDays();
        this.AppState.loading$.next(false);

        // Only subscribed to when pdf-modal opened
        this.pdf$ = this.Api.getTimecardPdf(this.timecard.timecardId);
      }, err => {
        console.error('Failed to get timecard details', err);
        if (err.status === 401) {
          this.Navigation.handleLogin();
        } else {
          this.Navigation.handleError(err);
        }
      });
  }

  populateTimecardDays() {

    // Map JS Day indices to Aquent day indices. JS Sunday is 0, Aquent Monday is 0.
    // Use elements' indices as object keys

    const dayIndexMap: number[] = [6, 0, 1, 2, 3, 4, 5];
    const endingDayIndex: number = dayIndexMap[this.timecard.weekEndingDate.getDay()];

    // Map the day names based on Monday being the zeroth day, use element's indices as object keys

    const dayNames: string[] = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
    ];

    const sundayIndex: number = 6;
    const numDays: number = 7;

    let dayIndex: number = endingDayIndex < sundayIndex ? endingDayIndex + 1 : 0;

    for (let i = 0; i < numDays; i++) {
        const dayAbbreviation: string = dayNames[dayIndex].substring(0, 3).toLowerCase();

        this.timecardDays.push({
            dayName: dayNames[dayIndex],
            timeIn: this.timecard[dayAbbreviation + 'TimeIn'],
            timeOut: this.timecard[dayAbbreviation + 'TimeOut'],
            timeLunch: this.timecard[dayAbbreviation + 'TimeLunch'],
            timeTotal: this.timecard[dayAbbreviation + 'TimeTotal'],
            marketId: this.timecard.marketId
        })

        dayIndex = dayIndex < sundayIndex ? dayIndex + 1 : 0;
    }
  }

  submitDispute(): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.disputeSubmitting = true;
    this.Analytics.trackEvent('Timecard detail: Timecard disputed');
    this.Api.disputeSingleTimecard({timecardDetails: this.timecard, reason: this.reason})
      .pipe(
        tap(() => {
          this.disputeModalClose();
          this.disputeSuccessModal.show();
          setTimeout(() => {
            this.disputeSuccessModal.hide();
          }, 3000);
          this.disputeSubmitting = false;
        }),
        switchMap(() => this.Api.getTimecardStatusDetails(this.timecard.timecardId))
      )
      .subscribe(data => {
        // FIXME bad; should reload/replace original timecard
        this.timecard.statusId = data.statusId;
        this.timecard.statusName = data.statusName;
        this.timecard.signed = data.signed;
        this.timecard.signedBy = data.signedBy;
        this.timecard.approved = data.approved;
        this.timecard.approvedBy = data.approvedBy;
      }, err => {
        this.error = true;
        console.error('Failed to dispute timecards', err);
        if (err.status === 401) {
          this.Navigation.handleLogin();
        } else {
          this.Navigation.handleError(err);
        }
      });
  }

  disputeModalClose(): void {
    this.reason = null;
    this.disputeModal.hide();
  }

  toggleDetails() {
    this.detailsExpanded$.next(!this.detailsExpanded$.getValue());
  }
}
